/**
 * useWorkdayStopFlow.js
 *
 * The one "End workday" confirmation flow shared by the header sheet, the briefing hero card and the Time Tracker strip.
 * Confirms through HappyAlert (never a native dialog). When a project / task timer is still running the dialog offers
 * the explicit choice "End workday + stop timer" vs "Only end workday".
 *
 * @module components/workday/useWorkdayStopFlow
 */

import {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import HappyAlert from '../HappyAlert';
import {useWorkday} from '../../helpers/WorkdayContext';
import {formatHM, workedTodaySec} from '../../helpers/workdayRules';

/**
 * @returns {{requestStop: () => Promise<boolean>}} `requestStop` resolves true when the workday was ended.
 */
export default function useWorkdayStopFlow() {
    const {t} = useTranslation();
    const {state, stop} = useWorkday();

    const requestStop = useCallback(async () => {
        const timerRunning = !!state?.timer?.running;
        const worked = formatHM(workedTodaySec(state || {}, Date.now()));
        const base = t('workday_confirm_text', {worked});

        if (!timerRunning) {
            const ok = await HappyAlert.confirm({
                title: t('workday_confirm_title'),
                message: base,
                confirmText: t('workday_confirm_stop'),
                cancelText: t('workday_cancel'),
            });

            return ok ? !!(await stop({stopTimers: false})) : false;
        }

        const choice = await new Promise((resolve) => {
            HappyAlert.show({
                type: 'question',
                title: t('workday_confirm_title'),
                message: `${base}\n${t('workday_confirm_with_timer', {task: state?.timer?.note || t('workday_running_timer')})}`,
                buttons: [
                    {text: t('workday_cancel'), style: 'cancel', onPress: () => resolve('cancel')},
                    {text: t('workday_confirm_stop'), style: 'default', onPress: () => resolve('only')},
                    {text: `${t('workday_confirm_stop')} + ${t('workday_stop_timer_too')}`, style: 'default', onPress: () => resolve('both')},
                ],
                onDismiss: () => resolve('cancel'),
            });
        });

        if (choice === 'cancel') {
            return false;
        }

        return !!(await stop({stopTimers: choice === 'both'}));
    }, [state, stop, t]);

    return {requestStop};
}
